import { useCallback, useEffect, useState } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { AppContainer } from '@presentation/App.styles'

import { InjectionTokens } from '@/controller/tokens'

import { ChangeTabProvider } from './contexts/ChangeTabContext'
import { useInjection } from './contexts/InjectionContext'
import { AppErrorBoundary } from './modules/Base/components/AppErrorBoundary/AppErrorBoundary'
import { BackdropGlobal } from './modules/Base/components/BackdropGlobal/BackdropGlobal'
import { BetaNotice } from './modules/Base/components/general/BetaNotice'
import { TouchDeviceAlert } from './modules/Base/components/TouchDeviceAlert/TouchDeviceAlert'
import {
  BannerPresenter,
  DrawerPresenter,
  FeedbackCollectorFeaturePresenter,
  FeedbackCollectorPresenter,
  FuelFormPresenter,
  InfoPanelPresenter,
  MeteorologyControlPresenter,
  ModalPresenter,
  ToolbarPresenter
} from './modules/Base/containers'
import { ChatPresenter } from './modules/Base/containers/ChatPresenter'
import { CustomTooltipPresenter } from './modules/Base/containers/CustomTooltip/CustomTooltipPresenter'
import { GlobalToastPresenter } from './modules/Base/containers/GlobalToastPresenter'
import { NetworkStatusPresenter } from './modules/Base/containers/NetworkStatusPresenter'
import { NewsBannerPresenter } from './modules/Base/containers/NewsBannerPresenter'
import { RouteGuidePresenter } from './modules/Base/containers/RouteGuidePresenter'

import { LocalStorageKeyValuePersister } from '@/infra/keyValuePersister/LocalStorageKeyValuePersister'
import { ILogger } from '@/infra/logger/ILogger'
import { MapPresenter } from '@/presentation/modules/MapScreen/containers'
import { isBetaFeatureEnabled, isTesting } from '@/utils/testing'

function App() {
  const persister = useInjection<LocalStorageKeyValuePersister>(InjectionTokens.KeyValuePersister)
  const logger = useInjection<ILogger>(InjectionTokens.Logger)
  const [deviceAlert, setDeviceAlert] = useState(false)

  useEffect(() => {
    const run = async () => {
      const consent = await persister.get('touch-alert-consent')
      if (consent.isFailure || (consent.isSuccess && consent.getValue() !== 'true')) {
        setDeviceAlert('ontouchstart' in window || navigator.maxTouchPoints > 0)
      }
    }

    run()
  }, [])

  const handlePersistConsent = useCallback(async () => {
    setDeviceAlert(false)
    const result = await persister.set('touch-alert-consent', 'true')
    if (result.isFailure) console.log(result.error)
  }, [])

  return (
    <AppContainer>
      {deviceAlert && <TouchDeviceAlert onClose={handlePersistConsent} onConsent={handlePersistConsent} />}
      <CustomTooltipPresenter />
      <BrowserRouter>
        <ChangeTabProvider>
          <DrawerPresenter />
        </ChangeTabProvider>
      </BrowserRouter>
      <AppErrorBoundary
        logger={logger}
        boundaryName="MapPresenterBoundary"
        title="Falha ao carregar o mapa"
        message="O mapa encontrou um erro durante a renderização. Recarregue o app para tentar novamente."
        variant="panel"
      >
        <MapPresenter />
      </AppErrorBoundary>
      <AppErrorBoundary
        logger={logger}
        boundaryName="ToolbarPresenterBoundary"
        title="Falha na barra de ferramentas"
        message="Os controles do mapa ficaram indisponíveis. Recarregue o app para restaurar essa área."
        variant="floatingTopRight"
      >
        <ToolbarPresenter />
      </AppErrorBoundary>
      <AppErrorBoundary
        logger={logger}
        boundaryName="MapOverlayBoundary"
        title="Falha em um painel do mapa"
        message="Um painel complementar do mapa parou de responder. Recarregue o app para tentar novamente."
        variant="floatingBottomCenter"
      >
        <>
          <InfoPanelPresenter />
          <FuelFormPresenter />
          <MeteorologyControlPresenter />
          <RouteGuidePresenter />
        </>
      </AppErrorBoundary>
      <ModalPresenter locale="global" />
      <GlobalToastPresenter />
      <BackdropGlobal />
      {/* <BannerPresenter /> TODO: habilitar o banner */}
      <NewsBannerPresenter />
      {/* Aviso global de conexao instavel e de dado aeronautico vencido em uso. */}
      <NetworkStatusPresenter />
      {isTesting() && <FeedbackCollectorPresenter />}
      {isBetaFeatureEnabled() && <FeedbackCollectorFeaturePresenter />}
      {isTesting() && <BetaNotice />}
      {!isTesting() && <ChatPresenter />}
    </AppContainer>
  )
}

export { App }
